import React, { useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  SELLER_APP_QR_URL,
  sellerAppStoreAnchorProps,
  sellerPlayStoreAnchorProps,
} from '../constants/appLinks';

const howItWorks = [
  {
    title: 'Post where buyers already shop',
    body: 'List on Facebook Marketplace, OfferUp, or your usual channels. Keep your photos, price, and description the way you already do.',
  },
  {
    title: 'Connect with local buyers',
    body: 'When someone is interested, answer questions and confirm the sale like you normally would.',
  },
  {
    title: 'Offer delivery through BuyDrop',
    body: 'Add BuyDrop so buyers can pay by card and get the item delivered instead of coordinating a meetup.',
  },
];

const benefits = [
  {
    title: 'Easier pickups',
    body: 'Fewer no-shows when buyers can choose delivery instead of driving across town.',
  },
  {
    title: 'Convenient delivery',
    body: 'You set your area and schedule. BuyDrop handles checkout and order tracking.',
  },
  {
    title: 'A better buying experience',
    body: 'Smooth delivery makes it more likely buyers come back for your next listing.',
  },
];

function scrollToHash(hash: string) {
  if (!hash || hash === '#') return;
  const id = hash.replace(/^#/, '');
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const SellerLocalListingsLanding: React.FC = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    document.title = 'Turn local listings into more sales — BuyDrop';
    scrollToHash(location.hash);
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen bg-white">
      <section className="relative border-b border-gray-100 bg-gradient-to-b from-green-50/70 to-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
            BuyDrop seller community
          </p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-[2.5rem] lg:leading-tight">
            Turn local listings into more sales
          </h1>
          <p className="mt-5 text-base leading-relaxed text-gray-600">
            You already sell on Facebook Marketplace and OfferUp. BuyDrop adds local delivery and
            card checkout so busy buyers can say yes without a long drive or a flaky meetup.
          </p>
          <div id="start-selling" className="scroll-mt-24 mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              {...sellerAppStoreAnchorProps()}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-xl bg-gray-900 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-gray-800"
            >
              Start selling on BuyDrop
            </a>
            <Link
              to="/sell"
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 transition hover:bg-gray-50"
            >
              Seller app overview
            </Link>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="scroll-mt-24 border-b border-gray-100 bg-gray-50/60">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
          <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">How it works</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            Post on Facebook Marketplace or OfferUp, connect with local buyers, and offer convenient
            delivery through BuyDrop.
          </p>
          <ol className="mt-8 space-y-4">
            {howItWorks.map((step, i) => (
              <li
                key={step.title}
                className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm"
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-green-700">
                  Step {i + 1}
                </span>
                <h3 className="mt-2 text-base font-semibold text-gray-900">{step.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-gray-100">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
          <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">Why sellers use it</h2>
          <ul className="mt-8 grid gap-4 sm:grid-cols-3">
            {benefits.map((item) => (
              <li
                key={item.title}
                className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
              >
                <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-b border-gray-100 bg-green-50/40">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 text-center sm:text-left">
          <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
            Start selling on BuyDrop
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            Download the seller app, connect payouts, and mention delivery in your listings.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              {...sellerPlayStoreAnchorProps()}
              className="inline-flex items-center justify-center rounded-xl bg-green-700 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-green-800"
            >
              Get the seller app
            </a>
            <span className="hidden sm:inline text-sm text-gray-500">or scan on your phone</span>
          </div>
          <div className="mt-6 inline-flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="rounded-lg bg-white p-1.5 ring-1 ring-gray-100 shrink-0">
              <QRCodeSVG
                value={SELLER_APP_QR_URL}
                size={76}
                level="M"
                marginSize={2}
                title="Scan to download BuyDrop Seller"
              />
            </div>
            <p className="text-sm text-left leading-relaxed text-gray-500">
              Opens the App Store or Google Play listing for BuyDrop Seller.
            </p>
          </div>
        </div>
      </section>

      <div className="border-t border-gray-100 bg-white">
        <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
          <p className="text-sm text-gray-500 text-center sm:text-left">
            Questions?{' '}
            <Link to="/help" className="font-medium text-gray-900 hover:underline">
              Help center
            </Link>
            {' · '}
            <Link to="/sell" className="font-medium text-gray-900 hover:underline">
              Seller app
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SellerLocalListingsLanding;
